import { Component, forwardRef, inject, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { Observable } from 'rxjs';

import { FirebaseService } from '../../services/firebase.service';

export const IMAGE_STORAGE_PATH = 'profile_pictures';

@Component({
	selector: 'dix-profile-picture-selector',
	templateUrl: './profile-picture-selector.component.html',
	styleUrls: ['./profile-picture-selector.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ProfilePictureSelectorComponent),
			multi: true,
		},
	],
	standalone: false,
})
export class ProfilePictureSelectorComponent implements ControlValueAccessor {
	private readonly storage = inject(FirebaseService).storage;

	isImageLoading = false;
	imageLoadingPercent$: Observable<number>;
	imageUrl: string;
	onChange: (arg: string) => void = () => {};
	onTouched: (arg: string) => void = () => {};
	_identifier: string;
	get identifier() {
		return this._identifier;
	}

	@Input()
	set identifier(value: string) {
		this._identifier = value;
		const file = ref(this.storage, `${IMAGE_STORAGE_PATH}/${this.identifier}.png`);
		getDownloadURL(file)
			.then((url) => {
				this.imageUrl = url;
			})
			.catch(() => {
				this.imageUrl = '';
			}); // clear on error
	}

	@Input() hideEditButton = false;

	writeValue(obj: string): void {
		this.imageUrl = obj;
	}

	registerOnChange(fn: (arg: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: (arg: string) => void): void {
		this.onTouched = fn;
	}

	uploadFile(event: Event): void {
		const element = event.target as HTMLInputElement;
		if (element.files.length > 0) {
			this.imageUrl = undefined;
			const file = element.files[0];
			const filePath = `${IMAGE_STORAGE_PATH}/${this.identifier}.png`;
			const storageRef = ref(this.storage, filePath);

			this.isImageLoading = true;
			const uploadTask = uploadBytesResumable(storageRef, file);

			this.imageLoadingPercent$ = new Observable((observer) => {
				uploadTask.on(
					'state_changed',
					(snapshot) => {
						const percent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
						observer.next(percent);
					},
					(error) => observer.error(error),
					() => observer.complete(),
				);
			});

			uploadTask
				.then(() => getDownloadURL(storageRef))
				.then((url) => {
					this.imageUrl = url as string;
					this.isImageLoading = false;

					this.onChange(this.imageUrl);
				})
				.catch(() => {
					this.isImageLoading = false;
				});
		}
	}
}
